import { Grid, TablePaginationConfig, Tag } from "antd";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { Column, RecordCountBadge, Table } from "components";
import {
  StyledTableHeader,
  StyledTableWrapper,
} from "components/table/table.styled";
import { RecurringBookingsResponse } from "features/bookings";
import { RecurringBookingsKey } from "features/bookings/components/bookings/types";
import { getUserFullName, usePermissions } from "features/users";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { RecurringBookingListItem } from "types";
import { bookingTypeTranslationsMap } from "utils/bookings";
import { DEFAULT_TIMEZONE, formatDate } from "utils/date";
// import { sourceTranslationsMap } from "utils/source";

const { useBreakpoint } = Grid;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Record<string, FilterValue>;
}

type BookingsTableProps = {
  isBookingListLoading: boolean;
  data?: RecurringBookingsResponse;
  tableParams: {
    pagination?: TablePaginationConfig;
  };
  setTableParams: Dispatch<SetStateAction<TableParams>>;
};

export const RenewalBookingsTable = ({
  data,
  isBookingListLoading,
  tableParams,
  setTableParams,
}: BookingsTableProps) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const permissions = usePermissions();

  if (!permissions) return null;

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<RecurringBookingListItem>
      | SorterResult<RecurringBookingListItem>[]
  ) => {
    setTableParams({
      pagination,
      ...sorter,
    });

    return {
      pagination,
      filters,
      ...sorter,
    };
  };

  return (
    <StyledTableWrapper>
      <StyledTableHeader>
        <RecordCountBadge
          text={t("bookings.foundBookings")}
          count={data?.total || 0}
        />
      </StyledTableHeader>
      <Table<RecurringBookingListItem>
        loading={isBookingListLoading}
        dataSource={data?.items}
        rowKey="id"
        onChange={handleTableChange}
        pagination={{
          current: tableParams.pagination?.current,
          defaultPageSize: tableParams.pagination?.pageSize,
          total: data?.total,
        }}
        scroll={{ x: 1440 }}
        // onRow={(record) => {
        //   return {
        //     onClick: () => {
        //       const selection = window?.getSelection()?.toString();

        //       if (!selection?.length) {
        //         navigate(getRoute(ROUTES.FIXED_BOOKING_DETAILS, record.id));
        //       }
        //     },
        //   };
        // }}
      >
        <Column<RecurringBookingListItem>
          title={t("bookings.table.start")}
          dataIndex={"date_from"}
          key={RecurringBookingsKey.Start}
          width={screens.xxl ? "auto" : 120}
          sorter={true}
          sortDirections={["ascend", "descend", "ascend"]} // workaround to remove null as an option
          defaultSortOrder={"descend"}
          render={(_, record) => (
            <>
              {formatDate({
                date: record.date_from,
                format: "date",
                timeZone: DEFAULT_TIMEZONE,
              })}
            </>
          )}
        />
        <Column<RecurringBookingListItem>
          title={t("bookings.table.end")}
          dataIndex={"date_to"}
          key={RecurringBookingsKey.End}
          width={screens.xxl ? "auto" : 120}
          sorter={true}
          sortDirections={["ascend", "descend", "ascend"]} // workaround to remove null as an option
          defaultSortOrder={"descend"}
          render={(_, record) => (
            <>
              {formatDate({
                date: record.date_to,
                format: "date",
                timeZone: DEFAULT_TIMEZONE,
              })}
            </>
          )}
        />
        {permissions.company.view ? (
          <Column<RecurringBookingListItem>
            title={t("bookings.table.company")}
            dataIndex={["field", "facility", "company", "name"]}
            width={screens.xxl ? "auto" : 200}
          />
        ) : (
          <Column<RecurringBookingListItem>
            title={t("bookings.table.facility")}
            dataIndex={["field", "facility", "name"]}
            width={screens.xxl ? "auto" : 200}
            render={(_, record) => record.field.name}
          />
        )}
        <Column<RecurringBookingListItem>
          title={t("bookings.table.field")}
          dataIndex={["field", "name"]}
        />
        <Column<RecurringBookingListItem>
          title={t("bookings.table.customerName")}
          dataIndex={"user"}
          width={screens.xxl ? "auto" : 200}
          render={(_, record) =>
            getUserFullName({
              first_name: record.first_name,
              last_name: record.last_name,
            })
          }
        />
        <Column<RecurringBookingListItem>
          title={t("bookings.table.phone")}
          dataIndex={"phone_number"}
        />
        <Column<RecurringBookingListItem>
          title={t("bookings.table.source")}
          dataIndex={"source"}
          render={(_, record) => <Tag>{record.source}</Tag>}
        />
        <Column<RecurringBookingListItem>
          title={t("bookings.table.bookingType")}
          dataIndex={"type"}
          width={144}
          render={(_, record) => bookingTypeTranslationsMap(record.type)}
        />
        <Column<RecurringBookingListItem>
          title={"Payment Status"}
          dataIndex={"status"}
          width={144}
        />
        {/* <Column<RecurringBookingListItem>
          title={t("bookings.table.count")}
          dataIndex={"count"}
          width={120}
          align={"center"}
          render={(_, record) => (
            <RecordCountBadge count={record.bookings_count} text={""} />
          )}
        /> */}
      </Table>
    </StyledTableWrapper>
  );
};
